/**
 * Display formatting.
 *
 * Two kinds of value arrive from the API and they are not interchangeable:
 *
 * - **Timestamps** (`created_at`, `completed_at`) are instants, stored UTC and
 *   serialised without a zone suffix. They must be marked as UTC before the
 *   browser reads them, or `new Date("2026-08-18T21:00:00")` is silently
 *   interpreted as local time and lands seven hours out.
 * - **Due dates** (`due_date`) are calendar dates with no instant at all. They
 *   are parsed as plain numbers, never through Date's timezone handling --
 *   `new Date("2026-08-20")` is midnight *UTC*, which is the 19th in Denver.
 *
 * Getting either wrong produces off-by-one-day bugs that look like nothing for
 * most of the day.
 */

import type { ItemPriority, ItemStatus } from '@/api/types'

/** Parse a naive-UTC timestamp from the API into a real instant. */
export function parseTimestamp(value: string): Date {
  const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
  return new Date(withZone)
}

/** Parse a YYYY-MM-DD date without letting the timezone touch it. */
export function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1)
}

/** Today as YYYY-MM-DD in the browser's zone, for comparing against due dates. */
export function todayIso(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** Whole days from `fromIso` to `toIso`, both YYYY-MM-DD. Negative = past. */
export function daysBetween(fromIso: string, toIso: string): number {
  const from = parseDateOnly(fromIso)
  const to = parseDateOnly(toIso)
  return Math.round((to.getTime() - from.getTime()) / 86_400_000)
}

export interface DueLabel {
  text: string
  /** Past due and still open. Renders in crimson wherever it appears. */
  overdue: boolean
  /** Due today or tomorrow: worth drawing the eye without alarm. */
  soon: boolean
}

export function formatDue(dueIso: string | null, today = todayIso()): DueLabel | null {
  if (!dueIso) return null
  const delta = daysBetween(today, dueIso)

  if (delta < 0) {
    const days = Math.abs(delta)
    return {
      text: days === 1 ? 'Overdue by 1 day' : `Overdue by ${days} days`,
      overdue: true,
      soon: false,
    }
  }
  if (delta === 0) return { text: 'Due today', overdue: false, soon: true }
  if (delta === 1) return { text: 'Due tomorrow', overdue: false, soon: true }
  if (delta <= 6) {
    return {
      text: `Due ${parseDateOnly(dueIso).toLocaleDateString(undefined, { weekday: 'long' })}`,
      overdue: false,
      soon: false,
    }
  }
  return { text: `Due ${formatDateOnly(dueIso)}`, overdue: false, soon: false }
}

export function formatDateOnly(value: string): string {
  const date = parseDateOnly(value)
  const sameYear = date.getFullYear() === new Date().getFullYear()
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export function formatTimestamp(value: string): string {
  return parseTimestamp(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** "Added 3 days ago" for the age column. */
export function formatAge(value: string): string {
  const days = Math.floor((Date.now() - parseTimestamp(value).getTime()) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.floor(days / 30)
  if (months < 12) return months === 1 ? 'a month ago' : `${months} months ago`
  const years = Math.floor(days / 365)
  return years === 1 ? 'a year ago' : `${years} years ago`
}

export const PRIORITY_LABEL: Record<ItemPriority, string> = {
  imminent: 'Imminent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

export const STATUS_LABEL: Record<ItemStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  dropped: 'Dropped',
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`)
}
